import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { TextInput } from '@ipfabric/ui-components/TextInput/TextInput';
import { withTargetValue } from '@ipfabric/ui-components/utils/events/withTargetValue';
import {
  ESCAPE,
  isAnyEnter,
} from '@ipfabric/ui-components/utils/keyboard/keyboardKeys';
import { isDateTimeTableColumnFormat } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { DateTimePicker } from 'components/DateTimePicker/DateTimePicker';
import { DropdownMenu } from 'components/DropdownMenu/DropdownMenu';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { getFlatStringFilterValue } from 'components/Table/components/SimpleFilter/helpers/getFlatStringFilterValue';
import { format } from 'date-fns';
import { useRef, useState } from 'react';
import type { FC, KeyboardEvent } from 'react';
import {
  getAllowedOperators,
  operatorReadableMap,
} from './helpers/filterOperators';
import { getFilterFromReadable } from './helpers/getFilterFromReadable';
import { getOperatorFromFilter } from './helpers/getOperatorFromFilter';
import type { ChangeFilterEvent } from './types/ChangeFilterEvent';
import type { SimpleFilterField } from './types/SimpleFilterField';

interface Props {
  filterField: SimpleFilterField;
  onChangeFilter: (command: ChangeFilterEvent) => void;
  onSubmitFilters: () => void;
  autoFocus?: boolean;
}

export const SimpleDateTimeFilter: FC<Props> = (props) => {
  const [focused, setFocused] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const ctxProps = useGlobalStateContextSelectors({
    dateFormat: prop('dateFormat'),
    dateTimeFormat: prop('dateTimeFormat'),
    isoFormat: prop('isoFormat'),
    timezone: prop('user', 'timezone'),
  });

  let formFieldFormat = ctxProps.dateTimeFormat; // fallback date value format

  if (isDateTimeTableColumnFormat(props.filterField.format)) {
    formFieldFormat = ctxProps[props.filterField.format.format];
  } else {
    stumble(
      `The '${props.filterField.column}' table is in unexpected format ('dateTime' expected).`,
    );
  }

  const getFormattedDate = (date: number) => {
    return format(date, formFieldFormat);
  };

  const handleChange = (value: string) => {
    props.onChangeFilter({
      column: props.filterField.column,
      value: value ?? null,
    });
  };

  const changeCalendarValue = (filter: { operator: string; value: number }) => {
    const { operator, value } = filter;
    handleChange(`${operator}${getFormattedDate(value)}`);
    setCalendarOpen(false);
    props.onSubmitFilters();
  };

  const handleClear = async () => {
    await props.onChangeFilter({
      column: props.filterField.column,
      value: null,
    });

    props.onSubmitFilters();

    inputRef.current?.blur();
    setFocused(false);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.code === ESCAPE) {
      handleClear();
    }
    if (isAnyEnter(e.code)) {
      props.onSubmitFilters();
    }
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const setOperator = (operator: string) => {
    const filterValue = getFilterFromReadable(
      props.filterField.filter,
      props.filterField.value,
    );

    handleChange(filterValue?.[1] ? `${operator}${filterValue[1]}` : operator);
  };

  const operatorItems = getAllowedOperators(props.filterField.filter).map(
    (operator) => ({
      label: operatorReadableMap[operator],
      onClick: () => {
        setOperator(operatorReadableMap[operator]);
      },
    }),
  );

  const getInputValue = () => {
    const filterValue = getFilterFromReadable(
      props.filterField.filter,
      props.filterField.value,
    );
    if (typeof filterValue?.[1] === 'string') {
      return filterValue[1];
    }
    return '';
  };

  const filterValue = getFlatStringFilterValue(props.filterField.value);
  const operator = getOperatorFromFilter(props.filterField.filter, filterValue);
  return (
    <div onBlur={handleBlur}>
      <div className='ipf-simple-date-time-filter'>
        <TextInput
          type='search'
          value={filterValue}
          onChange={withTargetValue(handleChange)}
          onFocus={handleFocus}
          inputRef={inputRef}
          size='small'
          hasError={props.filterField.error}
          onClear={handleClear}
          onKeyDown={handleKeyDown}
          placeholder={`>=${formFieldFormat}`}
          autoFocus={props.autoFocus}
          endAdornment={
            <Icon
              size='xs'
              className='text-element-secondary cursor-pointer'
              glyph='fa-calendar-days'
              onClick={() => setCalendarOpen(true)}
            />
          }
        />
      </div>
      {focused && (
        <div onMouseDown={(e) => e.preventDefault()}>
          <DropdownMenu
            onClose={() => {
              inputRef.current?.focus();
            }}
            items={operatorItems}
          />
        </div>
      )}
      {calendarOpen && (
        <DateTimePicker
          onChange={changeCalendarValue}
          onClose={() => setCalendarOpen(false)}
          value={getInputValue()}
          comparator={operator === '' ? undefined : operator}
          outputDateFormat={ctxProps.dateFormat}
          inputDateFormats={[ctxProps.dateFormat, ctxProps.dateTimeFormat]}
          timezone={ctxProps.timezone}
        />
      )}
    </div>
  );
};
